import { startAuthentication, startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ReloadIcon } from "@radix-ui/react-icons";
import { Alert, AlertDescription } from "@quake/shared-ui/components/ui/alert";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@quake/shared-ui/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@quake/shared-ui/components/ui/dialog";
import { HumanDateWithTooltip } from "@quake/shared-ui/components/ui/human-date-with-tooltip";
import { Input } from "@quake/shared-ui/components/ui/input";
import { Label } from "@quake/shared-ui/components/ui/label";
import { Separator } from "@quake/shared-ui/components/ui/separator";
import { Switch } from "@quake/shared-ui/components/ui/switch";
import { toast } from "@quake/shared-ui/lib/sonner";
import CheckCircleIcon from "lucide-react/dist/esm/icons/check-circle";
import CalendarDaysIcon from "lucide-react/dist/esm/icons/calendar-days";
import ClipboardIcon from "lucide-react/dist/esm/icons/clipboard";
import FolderIcon from "lucide-react/dist/esm/icons/folder";
import KeyRoundIcon from "lucide-react/dist/esm/icons/key-round";
import MailIcon from "lucide-react/dist/esm/icons/mail";
import ShieldCheckIcon from "lucide-react/dist/esm/icons/shield-check";
import SmartphoneIcon from "lucide-react/dist/esm/icons/smartphone";
import Trash2Icon from "lucide-react/dist/esm/icons/trash-2";
import PlugZapIcon from "lucide-react/dist/esm/icons/plug-zap";
import { useEffect, useMemo, useState } from "react";
import type { RegistrationResponseJSON } from "@simplewebauthn/browser";
import { MicrosoftLogo } from "~/components/MicrosoftLogo";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { trpc } from "~/utils/trpc";

export const Route = createFileRoute("/_dashboard/$datasetSlug/settings/security")({
  component: RouteComponent,
});

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    if (error.name === "NotAllowedError") return "Passkey prompt was cancelled.";
    return error.message;
  }
  return "Passkey setup failed.";
};

const formatDeviceType = (deviceType: string) =>
  deviceType === "multiDevice"
    ? "Synced passkey"
    : deviceType === "singleDevice"
      ? "Single-device passkey"
      : deviceType;

function RouteComponent() {
  const { datasetSlug } = Route.useParams();
  const queryClient = useQueryClient();
  const { data: session } = useQuery(trpc.auth.getSession.queryOptions());
  const { data, isLoading } = useQuery(trpc.auth.listMfaMethods.queryOptions());
  const { data: mcpConnections, isLoading: isLoadingMcpConnections } = useQuery(
    trpc.openapiOauth.listMcpConnections.queryOptions(),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [generatedRecoveryCodes, setGeneratedRecoveryCodes] = useState<string[] | null>(null);
  const [isAddPasskeyDialogOpen, setIsAddPasskeyDialogOpen] = useState(false);
  const [isAddTotpDialogOpen, setIsAddTotpDialogOpen] = useState(false);
  const [isMicrosoftDialogOpen, setIsMicrosoftDialogOpen] = useState(false);
  const [mailEnabled, setMailEnabled] = useState(true);
  const [calendarsEnabled, setCalendarsEnabled] = useState(false);
  const [filesEnabled, setFilesEnabled] = useState(false);
  const [passkeyLabelDraft, setPasskeyLabelDraft] = useState("");
  const [totpLabelDraft, setTotpLabelDraft] = useState("Authenticator app");
  const [totpCode, setTotpCode] = useState("");
  const [recentMfaCode, setRecentMfaCode] = useState("");
  const [recentMfaError, setRecentMfaError] = useState<string | null>(null);
  const [isRecentMfaDialogOpen, setIsRecentMfaDialogOpen] = useState(false);
  const [connectionToRevoke, setConnectionToRevoke] = useState<string | null>(null);
  const [pendingTotpSetup, setPendingTotpSetup] = useState<{
    challengeId: string;
    qrCodeDataUrl: string;
    manualEntryKey: string;
  } | null>(null);
  const [pendingRegistrationResult, setPendingRegistrationResult] = useState<{
    challengeId: string;
    response: RegistrationResponseJSON;
  } | null>(null);
  const supportsWebAuthn = useMemo(() => browserSupportsWebAuthn(), []);
  const isAdmin = session?.user?.role === "Administrator";

  const microsoftAutoConnectQuery = useQuery({
    ...trpc.linkedAccounts.getMicrosoftAutoConnectStatus.queryOptions(),
    enabled: isAdmin,
  });

  useBreadCrumbs([{ title: "Security", link: `/${datasetSlug}/settings/security` }]);

  const handleSecurityMutationError = (error: { message: string }) => {
    if (error.message === "Recent MFA verification required") {
      setRecentMfaError(null);
      setRecentMfaCode("");
      setIsRecentMfaDialogOpen(true);
      return;
    }
    toast.error(error.message);
  };

  const finishRegistrationMutation = useMutation(trpc.auth.finishPasskeyRegistration.mutationOptions());
  const beginRegistrationMutation = useMutation(
    trpc.auth.beginPasskeyRegistration.mutationOptions({
      onMutate: () => {
        setErrorMessage(null);
      },
      onSuccess: async ({ challengeId, options }) => {
        try {
          const response = await startRegistration({ optionsJSON: options });
          setPendingRegistrationResult({ challengeId, response });
          setIsAddPasskeyDialogOpen(true);
        } catch (error) {
          setErrorMessage(getErrorMessage(error));
        }
      },
      onError: (error) => {
        if (error.message === "Recent MFA verification required") {
          handleSecurityMutationError(error);
          return;
        }
        setErrorMessage(error.message);
      },
    }),
  );

  const finishPasskeyAuthenticationMutation = useMutation(trpc.auth.finishPasskeyAuthentication.mutationOptions());
  const beginPasskeyAuthenticationMutation = useMutation(
    trpc.auth.beginPasskeyAuthentication.mutationOptions({
      onMutate: () => {
        setRecentMfaError(null);
      },
      onSuccess: async ({ challengeId, options }) => {
        try {
          const response = await startAuthentication({ optionsJSON: options });
          await finishPasskeyAuthenticationMutation.mutateAsync({ challengeId, response });
          await queryClient.invalidateQueries({ queryKey: trpc.auth.getSession.queryKey() });
          setIsRecentMfaDialogOpen(false);
          toast.success("MFA verified");
        } catch (error) {
          setRecentMfaError(getErrorMessage(error));
        }
      },
      onError: (error) => {
        setRecentMfaError(error.message);
      },
    }),
  );

  const verifyRecentTotpMutation = useMutation(
    trpc.auth.verifyTotpCode.mutationOptions({
      onMutate: () => {
        setRecentMfaError(null);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.auth.getSession.queryKey() });
        setRecentMfaCode("");
        setIsRecentMfaDialogOpen(false);
        toast.success("MFA verified");
      },
      onError: (error) => {
        setRecentMfaError(error.message);
      },
    }),
  );

  const deletePasskeyMutation = useMutation(
    trpc.auth.deletePasskey.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.auth.listMfaMethods.queryKey() });
        toast.success("Passkey deleted");
      },
      onError: (error) => {
        handleSecurityMutationError(error);
      },
    }),
  );

  const regenerateRecoveryCodesMutation = useMutation(
    trpc.auth.regenerateRecoveryCodes.mutationOptions({
      onSuccess: async (result) => {
        setGeneratedRecoveryCodes(result.recoveryCodes);
        await queryClient.invalidateQueries({ queryKey: trpc.auth.listMfaMethods.queryKey() });
        toast.success("Recovery codes regenerated");
      },
      onError: (error) => {
        handleSecurityMutationError(error);
      },
    }),
  );

  const startMicrosoftAutoConnectMutation = useMutation(
    trpc.linkedAccounts.getMicrosoftAutoConnectOAuthUrl.mutationOptions({
      onSuccess: ({ url }) => {
        window.location.href = url;
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  const disconnectMicrosoftAutoConnectMutation = useMutation(
    trpc.linkedAccounts.disconnectMicrosoftAutoConnect.mutationOptions({
      onSuccess: async ({ message }) => {
        await queryClient.invalidateQueries({ queryKey: trpc.linkedAccounts.getMicrosoftAutoConnectStatus.queryKey() });
        toast.success(message);
      },
      onError: (error) => {
        handleSecurityMutationError(error);
      },
    }),
  );

  const revokeMcpConnectionMutation = useMutation(
    trpc.openapiOauth.revokeMcpConnection.mutationOptions({
      onSuccess: async () => {
        setConnectionToRevoke(null);
        await queryClient.invalidateQueries({ queryKey: trpc.openapiOauth.listMcpConnections.queryKey() });
        toast.success("MCP connection revoked");
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  const beginTotpEnrollmentMutation = useMutation(
    trpc.auth.beginTotpEnrollment.mutationOptions({
      onSuccess: (result) => {
        setPendingTotpSetup({
          challengeId: result.challengeId,
          qrCodeDataUrl: result.qrCodeDataUrl,
          manualEntryKey: result.manualEntryKey,
        });
        setTotpCode("");
        setIsAddTotpDialogOpen(true);
      },
      onError: (error) => {
        handleSecurityMutationError(error);
      },
    }),
  );

  const finishTotpEnrollmentMutation = useMutation(
    trpc.auth.finishTotpEnrollment.mutationOptions({
      onSuccess: async (result) => {
        await queryClient.invalidateQueries({ queryKey: trpc.auth.listMfaMethods.queryKey() });
        if (result.recoveryCodes?.length) {
          setGeneratedRecoveryCodes(result.recoveryCodes);
        }
        setPendingTotpSetup(null);
        setTotpCode("");
        setTotpLabelDraft("Authenticator app");
        setIsAddTotpDialogOpen(false);
        toast.success("Authenticator app added");
      },
      onError: (error) => {
        handleSecurityMutationError(error);
      },
    }),
  );

  const deleteTotpMutation = useMutation(
    trpc.auth.deleteTotpAuthenticator.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.auth.listMfaMethods.queryKey() });
        toast.success("Authenticator app deleted");
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("ms_auto_connect_success");
    const error = params.get("error");

    if (success) {
      toast.success("Microsoft tenant connection saved");
      void queryClient.invalidateQueries({ queryKey: trpc.linkedAccounts.getMicrosoftAutoConnectStatus.queryKey() });
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (error) {
      toast.error(decodeURIComponent(error));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [queryClient]);

  useEffect(() => {
    if (!microsoftAutoConnectQuery.data || isMicrosoftDialogOpen) return;

    const hasSavedMicrosoftFeatureSelection =
      microsoftAutoConnectQuery.data.features.mail ||
      microsoftAutoConnectQuery.data.features.calendars ||
      microsoftAutoConnectQuery.data.features.files;

    setMailEnabled(hasSavedMicrosoftFeatureSelection ? microsoftAutoConnectQuery.data.features.mail : true);
    setCalendarsEnabled(microsoftAutoConnectQuery.data.features.calendars);
    setFilesEnabled(microsoftAutoConnectQuery.data.features.files);
  }, [microsoftAutoConnectQuery.data, isMicrosoftDialogOpen]);

  const isBusy =
    beginRegistrationMutation.isPending ||
    finishRegistrationMutation.isPending ||
    deletePasskeyMutation.isPending ||
    beginTotpEnrollmentMutation.isPending ||
    finishTotpEnrollmentMutation.isPending ||
    deleteTotpMutation.isPending ||
    regenerateRecoveryCodesMutation.isPending;
  const hasMicrosoftAutoConnectFeaturesEnabled = mailEnabled || calendarsEnabled || filesEnabled;

  const handleStartPasskeySetup = () => {
    setErrorMessage(null);
    beginRegistrationMutation.mutate();
  };

  const handleStartTotpSetup = () => {
    setErrorMessage(null);
    beginTotpEnrollmentMutation.mutate({ label: totpLabelDraft.trim() || "Authenticator app" });
  };

  const handleSavePasskey = async () => {
    if (!pendingRegistrationResult) return;

    try {
      const result = await finishRegistrationMutation.mutateAsync({
        challengeId: pendingRegistrationResult.challengeId,
        response: pendingRegistrationResult.response,
        label: passkeyLabelDraft.trim() || null,
      });

      await queryClient.invalidateQueries({ queryKey: trpc.auth.listMfaMethods.queryKey() });
      if (result.recoveryCodes?.length) {
        setGeneratedRecoveryCodes(result.recoveryCodes);
      }
      setPasskeyLabelDraft("");
      setPendingRegistrationResult(null);
      setIsAddPasskeyDialogOpen(false);
      toast.success("Passkey added");
    } catch (error) {
      if (error instanceof Error && error.message === "Recent MFA verification required") {
        handleSecurityMutationError(error);
        return;
      }
      setErrorMessage(getErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      <Dialog open={isAddPasskeyDialogOpen} onOpenChange={setIsAddPasskeyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Name this passkey</DialogTitle>
            <DialogDescription>
              Add a friendly name so you can recognize this device or security key later.
            </DialogDescription>
          </DialogHeader>

          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSavePasskey();
            }}
          >
            <div className="space-y-2">
              <Label htmlFor="passkey-name">Passkey name</Label>
              <Input
                id="passkey-name"
                value={passkeyLabelDraft}
                onChange={(event) => setPasskeyLabelDraft(event.target.value)}
                placeholder="Work MacBook, iPhone Face ID, YubiKey..."
                autoFocus
                maxLength={120}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsAddPasskeyDialogOpen(false);
                  setPendingRegistrationResult(null);
                  setPasskeyLabelDraft("");
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={finishRegistrationMutation.isPending}>
                {finishRegistrationMutation.isPending ? "Saving..." : "Save passkey"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isAddTotpDialogOpen}
        onOpenChange={(open) => {
          setIsAddTotpDialogOpen(open);
          if (!open) {
            setPendingTotpSetup(null);
            setTotpCode("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add authenticator app</DialogTitle>
            <DialogDescription>Scan the QR code and enter the current code from your app.</DialogDescription>
          </DialogHeader>

          {pendingTotpSetup ? (
            <div className="space-y-4">
              <div className="flex justify-center rounded-lg border bg-background p-4">
                <img src={pendingTotpSetup.qrCodeDataUrl} alt="Authenticator app QR code" className="h-48 w-48" />
              </div>
              <div className="space-y-2">
                <Label>Manual key</Label>
                <div className="break-all rounded-md border bg-muted/30 px-3 py-2 font-mono text-sm">
                  {pendingTotpSetup.manualEntryKey}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="totp-name">Name</Label>
                <Input
                  id="totp-name"
                  value={totpLabelDraft}
                  onChange={(event) => setTotpLabelDraft(event.target.value)}
                  maxLength={120}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="totp-code">Code</Label>
                <Input
                  id="totp-code"
                  value={totpCode}
                  onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center text-muted-foreground">
              <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
              Preparing setup...
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsAddTotpDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!pendingTotpSetup || totpCode.length !== 6 || finishTotpEnrollmentMutation.isPending}
              onClick={() => {
                if (!pendingTotpSetup) return;
                finishTotpEnrollmentMutation.mutate({
                  challengeId: pendingTotpSetup.challengeId,
                  code: totpCode,
                  label: totpLabelDraft.trim() || "Authenticator app",
                });
              }}
            >
              {finishTotpEnrollmentMutation.isPending ? "Verifying..." : "Verify and save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isRecentMfaDialogOpen} onOpenChange={setIsRecentMfaDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Verify MFA</DialogTitle>
            <DialogDescription>Confirm your identity before changing MFA methods.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {recentMfaError ? (
              <Alert className="bg-destructive/10 border-destructive">
                <AlertDescription>{recentMfaError}</AlertDescription>
              </Alert>
            ) : null}

            {(data?.passkeyCount ?? 0) > 0 ? (
              <Button
                type="button"
                className="w-full justify-start"
                disabled={!supportsWebAuthn || beginPasskeyAuthenticationMutation.isPending}
                onClick={() => beginPasskeyAuthenticationMutation.mutate()}
              >
                <KeyRoundIcon className="mr-2 h-4 w-4" />
                Use passkey
              </Button>
            ) : null}

            {(data?.totpCount ?? 0) > 0 ? (
              <div className="space-y-2">
                <Label htmlFor="recent-totp-code">Authenticator code</Label>
                <Input
                  id="recent-totp-code"
                  value={recentMfaCode}
                  onChange={(event) => setRecentMfaCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                />
                <Button
                  type="button"
                  variant={(data?.passkeyCount ?? 0) > 0 ? "outline" : "default"}
                  className="w-full"
                  disabled={recentMfaCode.length !== 6 || verifyRecentTotpMutation.isPending}
                  onClick={() => verifyRecentTotpMutation.mutate({ code: recentMfaCode })}
                >
                  {verifyRecentTotpMutation.isPending ? "Verifying..." : "Verify authenticator code"}
                </Button>
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setIsRecentMfaDialogOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isMicrosoftDialogOpen} onOpenChange={setIsMicrosoftDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Microsoft Auto Connect</DialogTitle>
            <DialogDescription>
              Grant tenant admin consent and save Microsoft tenant connection data for this dataset.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-1">
                <Label htmlFor="microsoft-auto-connect-mail">Mail</Label>
                <p className="text-sm text-muted-foreground">
                  Save mail-related Microsoft tenant scopes for this dataset.
                </p>
              </div>
              <Switch id="microsoft-auto-connect-mail" checked={mailEnabled} onCheckedChange={setMailEnabled} />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-1">
                <Label htmlFor="microsoft-auto-connect-calendars">Calendars</Label>
                <p className="text-sm text-muted-foreground">
                  Save calendar-related Microsoft tenant scopes for this dataset.
                </p>
              </div>
              <Switch
                id="microsoft-auto-connect-calendars"
                checked={calendarsEnabled}
                onCheckedChange={setCalendarsEnabled}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-1">
                <Label htmlFor="microsoft-auto-connect-files">Files</Label>
                <p className="text-sm text-muted-foreground">
                  Save file-related Microsoft tenant scopes for this dataset.
                </p>
              </div>
              <Switch id="microsoft-auto-connect-files" checked={filesEnabled} onCheckedChange={setFilesEnabled} />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsMicrosoftDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!hasMicrosoftAutoConnectFeaturesEnabled || startMicrosoftAutoConnectMutation.isPending}
              onClick={() => {
                startMicrosoftAutoConnectMutation.mutate({
                  mail: mailEnabled,
                  calendars: calendarsEnabled,
                  files: filesEnabled,
                });
              }}
            >
              {startMicrosoftAutoConnectMutation.isPending ? "Connecting..." : "Connect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(connectionToRevoke)} onOpenChange={(open) => !open && setConnectionToRevoke(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke MCP connection?</DialogTitle>
            <DialogDescription>
              Claude or Codex will immediately lose access to this Dataset and must be authorized again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConnectionToRevoke(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!connectionToRevoke || revokeMcpConnectionMutation.isPending}
              onClick={() => {
                if (connectionToRevoke) {
                  revokeMcpConnectionMutation.mutate({ connection_id: connectionToRevoke });
                }
              }}
            >
              {revokeMcpConnectionMutation.isPending ? "Revoking..." : "Revoke"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {generatedRecoveryCodes?.length ? (
        <Card>
          <CardHeader>
            <CardTitle>New Recovery Codes</CardTitle>
            <CardDescription>
              These codes replace any previous recovery codes. Save them before closing this page.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-2 rounded-lg border bg-muted/30 p-4 sm:grid-cols-2">
              {generatedRecoveryCodes.map((code) => (
                <div key={code} className="rounded-md border bg-background px-3 py-2 font-mono text-sm">
                  {code}
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  await navigator.clipboard.writeText(generatedRecoveryCodes.join("\n"));
                  toast.success("Recovery codes copied");
                }}
              >
                <ClipboardIcon className="mr-2 h-4 w-4" />
                Copy codes
              </Button>
              <Button type="button" variant="ghost" onClick={() => setGeneratedRecoveryCodes(null)}>
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {errorMessage ? (
        <Alert className="bg-destructive/10 border-destructive">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      {!supportsWebAuthn ? (
        <Alert className="bg-destructive/10 border-destructive">
          <AlertDescription>
            This browser does not support passkeys. Use a modern browser on a supported device.
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Passkeys</CardTitle>
          <CardDescription>Manage the passkeys allowed to access this dataset membership.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <ShieldCheckIcon className="h-4 w-4" />
              <span>{data?.passkeyCount ?? 0} enrolled</span>
            </div>
            <Button type="button" disabled={!supportsWebAuthn || isBusy} onClick={handleStartPasskeySetup}>
              {isBusy ? (
                <>
                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                  Working...
                </>
              ) : (
                <>
                  <KeyRoundIcon className="mr-2 h-4 w-4" />
                  Add passkey
                </>
              )}
            </Button>
          </div>

          <Separator />

          {isLoading ? (
            <div className="flex items-center text-muted-foreground">
              <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
              Loading passkeys...
            </div>
          ) : data?.passkeys?.length ? (
            <div className="space-y-3">
              {data.passkeys.map((passkey) => (
                <div
                  key={passkey.id}
                  className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="space-y-1">
                    <p className="font-medium">{passkey.label || formatDeviceType(passkey.deviceType)}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDeviceType(passkey.deviceType)}
                      {passkey.backedUp ? " • backed up" : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Last used: {passkey.lastUsedAt ? <HumanDateWithTooltip date={passkey.lastUsedAt} /> : "Never"}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isBusy || (data.primaryMethodCount ?? 0) <= 1}
                    onClick={() => deletePasskeyMutation.mutate({ passkeyId: passkey.id })}
                  >
                    <Trash2Icon className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No passkeys enrolled yet.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Authenticator Apps</CardTitle>
          <CardDescription>Use time-based codes from an authenticator app as an MFA method.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <SmartphoneIcon className="h-4 w-4" />
              <span>{data?.totpCount ?? 0} enrolled</span>
            </div>
            <Button type="button" variant="outline" disabled={isBusy} onClick={handleStartTotpSetup}>
              {beginTotpEnrollmentMutation.isPending ? (
                <>
                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                  Preparing...
                </>
              ) : (
                <>
                  <SmartphoneIcon className="mr-2 h-4 w-4" />
                  Add authenticator app
                </>
              )}
            </Button>
          </div>

          <Separator />

          {isLoading ? (
            <div className="flex items-center text-muted-foreground">
              <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
              Loading authenticator apps...
            </div>
          ) : data?.totpAuthenticators?.length ? (
            <div className="space-y-3">
              {data.totpAuthenticators.map((authenticator) => (
                <div
                  key={authenticator.id}
                  className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="space-y-1">
                    <p className="font-medium">{authenticator.label || "Authenticator app"}</p>
                    <p className="text-xs text-muted-foreground">
                      Last used:{" "}
                      {authenticator.lastUsedAt ? <HumanDateWithTooltip date={authenticator.lastUsedAt} /> : "Never"}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isBusy || (data.primaryMethodCount ?? 0) <= 1}
                    onClick={() => deleteTotpMutation.mutate({ totpId: authenticator.id })}
                  >
                    <Trash2Icon className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No authenticator apps enrolled yet.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recovery Codes</CardTitle>
          <CardDescription>Recovery codes are a fallback if your MFA method is unavailable.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {data?.remainingRecoveryCodes ?? 0} unused recovery{" "}
            {(data?.remainingRecoveryCodes ?? 0) === 1 ? "code" : "codes"} remaining.
          </p>
          <Button
            type="button"
            variant="outline"
            disabled={isBusy || (data?.primaryMethodCount ?? 0) === 0}
            onClick={() => regenerateRecoveryCodesMutation.mutate()}
          >
            Regenerate recovery codes
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PlugZapIcon className="h-5 w-5" />
            <span>MCP Connections</span>
          </CardTitle>
          <CardDescription>Review Claude and Codex access authorized with your Quake account.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingMcpConnections ? (
            <div className="flex items-center text-sm text-muted-foreground">
              <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
              Loading connections...
            </div>
          ) : mcpConnections?.length ? (
            <div className="divide-y rounded-md border">
              {mcpConnections.map((connection) => (
                <div
                  key={connection.id}
                  className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      <span>{connection.client}</span>
                      <span className="text-muted-foreground">{connection.dataset_name}</span>
                      <span className="text-xs capitalize text-muted-foreground">{connection.status}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {connection.granted_scopes.join(", ")} · Authorized{" "}
                      <HumanDateWithTooltip date={connection.authorized_at} /> · Last used{" "}
                      {connection.last_used_at ? <HumanDateWithTooltip date={connection.last_used_at} /> : "Never"}
                    </p>
                  </div>
                  {connection.status === "active" ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setConnectionToRevoke(connection.id)}
                    >
                      <Trash2Icon className="mr-2 h-4 w-4" />
                      Revoke
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No MCP connections yet.</p>
          )}
        </CardContent>
      </Card>

      {isAdmin ? (
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <MicrosoftLogo className="h-5 w-5 shrink-0" />
                  <span>Microsoft Auto Connect</span>
                </CardTitle>
                <CardDescription>
                  Connect Microsoft once at dataset level and store the tenant connection.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {microsoftAutoConnectQuery.data?.isConnected ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4 text-emerald-600" />
                    <span className="text-emerald-700">Connected</span>
                  </>
                ) : (
                  <span className="text-muted-foreground">Not connected</span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {microsoftAutoConnectQuery.isError ? (
              <>
                <Alert className="bg-destructive/10 border-destructive">
                  <AlertDescription className="text-destructive">
                    Failed to load Microsoft connection status: {microsoftAutoConnectQuery.error.message}
                  </AlertDescription>
                </Alert>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void microsoftAutoConnectQuery.refetch()}
                    disabled={microsoftAutoConnectQuery.isRefetching}
                  >
                    <ReloadIcon
                      className={`mr-2 h-4 w-4 ${microsoftAutoConnectQuery.isRefetching ? "animate-spin" : ""}`}
                    />
                    Retry
                  </Button>
                  <Button type="button" disabled variant="outline" size="sm">
                    <MicrosoftLogo className="mr-2 h-4 w-4 shrink-0" />
                    Connect Microsoft
                  </Button>
                </div>
              </>
            ) : microsoftAutoConnectQuery.isLoading ? (
              <div className="flex items-center text-muted-foreground">
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                Loading connection...
              </div>
            ) : (
              <>
                <div className="rounded-lg border p-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <MailIcon className="h-4 w-4" />
                      Mail
                      {microsoftAutoConnectQuery.data?.features.mail ? (
                        <span className="text-emerald-700">Enabled</span>
                      ) : (
                        <span className="text-muted-foreground">Disabled</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <CalendarDaysIcon className="h-4 w-4" />
                      Calendars
                      {microsoftAutoConnectQuery.data?.features.calendars ? (
                        <span className="text-emerald-700">Enabled</span>
                      ) : (
                        <span className="text-muted-foreground">Disabled</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <FolderIcon className="h-4 w-4" />
                      Files
                      {microsoftAutoConnectQuery.data?.features.files ? (
                        <span className="text-emerald-700">Enabled</span>
                      ) : (
                        <span className="text-muted-foreground">Disabled</span>
                      )}
                    </div>
                  </div>

                  {microsoftAutoConnectQuery.data?.isConnected ? (
                    <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                      <p>Microsoft tenant connection active.</p>
                      <p>
                        Connected:{" "}
                        {microsoftAutoConnectQuery.data.connectedAt ? (
                          <HumanDateWithTooltip date={microsoftAutoConnectQuery.data.connectedAt} />
                        ) : (
                          "Unknown"
                        )}
                      </p>
                      {microsoftAutoConnectQuery.data.tenantId ? (
                        <p>Tenant ID: {microsoftAutoConnectQuery.data.tenantId}</p>
                      ) : null}
                      {microsoftAutoConnectQuery.data.objectId ? (
                        <p>Object ID: {microsoftAutoConnectQuery.data.objectId}</p>
                      ) : null}
                      {microsoftAutoConnectQuery.data.applicationId ? (
                        <p>Application ID: {microsoftAutoConnectQuery.data.applicationId}</p>
                      ) : null}
                      {microsoftAutoConnectQuery.data.mainAdminUserId ? (
                        <p>Connected by: {microsoftAutoConnectQuery.data.mainAdminUserId}</p>
                      ) : null}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-muted-foreground">
                      No dataset-level Microsoft connection saved yet.
                    </p>
                  )}
                </div>

                <Button type="button" onClick={() => setIsMicrosoftDialogOpen(true)}>
                  <MicrosoftLogo className="mr-2 h-4 w-4 shrink-0" />
                  {microsoftAutoConnectQuery.data?.isConnected ? "Reconnect Microsoft" : "Connect Microsoft"}
                </Button>
                {microsoftAutoConnectQuery.data?.isConnected ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={disconnectMicrosoftAutoConnectMutation.isPending}
                    onClick={() => disconnectMicrosoftAutoConnectMutation.mutate()}
                    className="ml-2"
                  >
                    <Trash2Icon className="mr-2 h-4 w-4" />
                    {disconnectMicrosoftAutoConnectMutation.isPending ? "Disconnecting..." : "Disconnect"}
                  </Button>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
